/*
 * CAPTURE-OWNED MODULE
 * Read-only "Document attributes" block at the top of Template Studio's field
 * rail. Shows the template's document type by its registry label (the same
 * source and rule DocTypeSelector uses: GET /templates/document-types, matched
 * by key). The type is changed in the editor header, not here.
 */
import React, { useEffect, useState } from 'react';
import { captureAPI } from '../../services/api';

/**
 * @param {object}  props
 * @param {string}  props.documentType      capture_templates.document_type (key)
 * @param {object}  [props.attributeField]  a template field keyed document_type, if one exists
 */
export default function DocumentAttributesSection({ documentType, attributeField = null }) {
  const [docTypes, setDocTypes] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await captureAPI.getDocumentTypes();
        if (!cancelled) setDocTypes(res?.documentTypes || []);
      } catch {
        if (!cancelled) setDocTypes([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // DocTypeSelector lists active types by label and shows any other value as
  // "<key> (inactive)". Same rule here; while the list loads, show the key.
  let typeText = 'Not set';
  if (documentType) {
    const active = (docTypes || []).find((dt) => dt.key === documentType && dt.is_active);
    if (active) typeText = active.label || documentType;
    else if (docTypes === null) typeText = documentType;
    else typeText = `${documentType} (inactive)`;
  }

  return (
    // Compact: one visible line ("Document type: Invoice"). The section title
    // and the hint stay in the DOM for screen readers; the hint is also the
    // row's tooltip.
    <div className="document-attributes-section" data-testid="document-attributes">
      <div className="document-attributes-title visually-hidden">Document attributes</div>
      <div className="document-attributes-row" title="Change it with Document Type in the header.">
        <span className="document-attributes-name">Document type:</span>{' '}
        <span className="document-attributes-value">{typeText}</span>
      </div>
      <div className="document-attributes-hint visually-hidden">Change it with Document Type in the header.</div>
      {attributeField && (
        <div className="document-attributes-notice" role="note">
          {attributeField.enabled === false
            ? 'This template also has a document_type field. It is ignored here and is not extracted.'
            : 'This template also has a document_type field. It is ignored here but is still extracted while enabled.'}
        </div>
      )}
    </div>
  );
}
